import { GenericServices } from "./generic.services";
import marketingUri from "../common/uri/marketing-uri.constants";
import { ApiResponse } from "../models/app-configuration";
import {
	IMarketingDashboardData,
	IMarketingFilter,
	IMarketingFilterOptions,
	IMarketingPaged,
	IMarketingCampaignRow,
	IMarketingTrackingRecord
} from "../models/marketing/marketing-tracking.model";

/**
 * Marketing tracking service (FRO-10957) — backed by the universal-service
 * endpoints from BAC-2786. Standard contract: the global axios interceptor wraps
 * every 200 in `ApiResponse { isSuccess, data }`, so the payload is `res.data`
 * (same one-level peel every other service uses).
 */
export class MarketingServices extends GenericServices {
	constructor() {
		super();
	}

	/** Aggregated dashboard payload (summary, daily, top sources, campaigns, records). */
	getDashboard(filter?: IMarketingFilter): Promise<IMarketingDashboardData> {
		return super.executeSelectingPost(filter || {}, marketingUri.dashboard)
			.then((res: ApiResponse) => res && res.isSuccess
				? Promise.resolve(res.data as IMarketingDashboardData)
				: Promise.reject((res && res.message) || "Invalid marketing dashboard response"));
	}

	/** A page of tracking records ("view all" / load more). */
	getTrackingRecords(filter: IMarketingFilter, pageIndex: number, pageSize: number): Promise<IMarketingPaged<IMarketingTrackingRecord>> {
		return this.getPaged<IMarketingTrackingRecord>(marketingUri.trackingRecords, filter, pageIndex, pageSize);
	}

	/** A page of campaigns ("view all" / load more). */
	getCampaigns(filter: IMarketingFilter, pageIndex: number, pageSize: number): Promise<IMarketingPaged<IMarketingCampaignRow>> {
		return this.getPaged<IMarketingCampaignRow>(marketingUri.campaigns, filter, pageIndex, pageSize);
	}

	/**
	 * Shared paged fetch using the project-standard request/response shape:
	 * body `{ Condition, PageIndex (1-based), PageSize, OrderBy }`. The interceptor
	 * turns a paged response (x-pagination-* headers) into a BaseListing
	 * (Records -> listOfItems, Pagination -> pagination { total }). Mapped to { Items, Total }.
	 */
	private getPaged<T>(path: string, filter: IMarketingFilter, pageIndex: number, pageSize: number): Promise<IMarketingPaged<T>> {
		const body = { Condition: filter || {}, PageIndex: pageIndex, PageSize: pageSize, OrderBy: "" };
		return super.executeSelectingPost(body, path).then((res: ApiResponse) => {
			if (!res || !res.isSuccess) {
				return Promise.reject((res && res.message) || "Invalid paged response");
			}
			const paged: any = res.data || {};
			const records = paged.listOfItems || paged.Records || [];
			const pag = paged.pagination || paged.Pagination || {};
			const total = pag.total != null ? pag.total : (pag.TotalRecords != null ? pag.TotalRecords : records.length);
			return Promise.resolve({ Items: records as T[], Total: total });
		});
	}

	/** Distinct values to populate the source/campaign/term filter dropdowns. */
	getFilterOptions(): Promise<IMarketingFilterOptions> {
		return super.executeSelecting(undefined, marketingUri.filterOptions)
			.then((res: ApiResponse) => res && res.isSuccess
				? Promise.resolve(res.data as IMarketingFilterOptions)
				: Promise.reject((res && res.message) || "Invalid filter-options response"));
	}
}

export default new MarketingServices();
